import { Injectable } from '@angular/core';

import { Observable, throwError } from 'rxjs';
import {
  catchError,
  distinctUntilChanged,
  filter,
  map,
  mergeMap,
  retry,
  shareReplay,
} from 'rxjs/operators';

import { IResponseItems } from 'src/app/shared/interfaces/response.interfaces';
import {
  ResponseReason,
  Reason,
  ReasonMessageType,
} from 'src/app/shared/model/refusal-reasons.model';
import {
  API_REASONS_MESSAGES,
  ApiService,
  CATEGORIES,
} from 'src/app/core/api.service';
import { SessionService } from 'src/app/core/session.service';

@Injectable({
  providedIn: 'root',
})
export class RefusalReasonService {
  private categories$?: Observable<ResponseReason[]>;

  constructor(
    private apiService: ApiService,
    private sessionService: SessionService
  ) {
    // Dropped when the company changes. This service is `providedIn: 'root'`
    // and nothing reloads the app on that path, so without this the cache
    // crosses into the next company in the same tab.
    //
    // Which emissions arrive is not obvious: neither logout touches this
    // stream. The reset to `null` comes from `CompanyPickerGuard` on the way
    // into `/companies`, and picking the next company emits again;
    // `changeCurrentCompany` short-circuits on an unchanged id, so re-entering
    // the same company emits nothing -- same taxonomy, nothing to invalidate.
    this.sessionService.currentCompanyId$
      .pipe(distinctUntilChanged())
      .subscribe(() => (this.categories$ = undefined));

    // And on the account, which the company id cannot see: a logout and a login
    // into a different account on the *same* company leaves
    // `currentCompanyId$` unchanged, so the second operator would inherit the
    // first one's taxonomy. Two independent subscriptions rather than a
    // `combineLatest`, so a stream that has not emitted cannot hold the other's
    // invalidation hostage.
    this.sessionService.loggedInUser$
      .pipe(
        map((user) => user?.id),
        distinctUntilChanged()
      )
      .subscribe({
        next: () => (this.categories$ = undefined),
        // `loggedInUser$` is `shareReplay(1)` over a bare `api.get` with no
        // retry, so one 5xx kills it for the session -- and a bare subscriber
        // would report that to Sentry a second time, on top of the handler
        // `Analytics` already has on this stream. Dropping the cache is the
        // conservative half: with the stream dead we can no longer tell that
        // the account changed.
        error: () => (this.categories$ = undefined),
      });
  }

  getCategories(): Observable<ResponseReason[]> {
    // Shared for the session instead of refetched -- the refusal dialog asks on
    // every open -- and what it buys is one request per opening, which is why
    // the invalidation above has to stay tight. A failed load is deliberately
    // not shared: `shareReplay` would replay the error to every later caller.
    //
    // **Safe only while the refusal taxonomy has no tenant scope on the
    // server**, which covers both `reasons_categories` *and* the `reasons`
    // children the dialog actually offers. It has none today. The evidence, and
    // what breaks silently if that changes, is recorded on the tech-matrix card
    // under "Cross-service invariants the frontend relies on" and is
    // deliberately not re-derived here, because a comment asserting another
    // repository's schema reads as verification while being a claim nobody
    // re-checks:
    // https://github.com/closecare/tech-matrix/blob/main/repositories/rh/web-b2b.md
    //
    // What the session scope still costs: a category renamed in backoffice needs
    // a page reload to appear. That rename is what the reprove event reports as
    // `preselection_outcome: 'unresolved'`.
    if (!this.categories$) {
      const request = this.apiService
        .get<IResponseItems<ResponseReason>>(CATEGORIES)
        .pipe(
          map(({ items }) => items),
          catchError((error) => {
            // Only if this is still the cached one. A company change while the
            // request is in flight invalidates the field and the next caller
            // installs a fresh observable; clearing unconditionally would throw
            // that newer one away and cost the new company a second request.
            if (this.categories$ === request) this.categories$ = undefined;

            return throwError(() => error);
          }),
          shareReplay({ bufferSize: 1, refCount: false })
        );

      this.categories$ = request;
    }

    return this.categories$;
  }

  getList(
    { reasonIds, type }: { reasonIds?: number[]; type?: ReasonMessageType } = {
      type: 'refusal',
    }
  ): Observable<Reason[]> {
    const reason_ids = reasonIds?.map((id) => id.toString()) || [];

    if (type === undefined) type = 'refusal';

    return this.sessionService.currentCompanyId$.pipe(
      filter((companyId) => !!companyId),
      mergeMap((companyId) =>
        this.apiService
          .get<{
            items: Reason[];
          }>(API_REASONS_MESSAGES(companyId), { params: { reason_ids, type } })
          .pipe(
            retry(2),
            map(({ items }) => items),
            catchError(() => [])
          )
      )
    );
  }

  add(reason: Reason): Observable<{ id: number }> {
    return this.sessionService.currentCompanyId$.pipe(
      filter((companyId) => !!companyId),
      mergeMap((companyId: string) =>
        this.apiService.post<{ id: number }>(
          API_REASONS_MESSAGES(companyId),
          reason
        )
      )
    );
  }

  update(
    reasonId: string,
    changes: Partial<Reason>
  ): Observable<{ id: number }> {
    return this.sessionService.currentCompanyId$.pipe(
      filter((companyId) => !!companyId),
      mergeMap((companyId: string) => {
        const api: string = API_REASONS_MESSAGES(companyId, reasonId);

        return this.apiService.put<{ id: number }>(api, changes);
      })
    );
  }

  delete(reasonId: string): Observable<null> {
    return this.sessionService.currentCompanyId$.pipe(
      filter((companyId) => !!companyId),
      mergeMap((companyId: string) => {
        const api: string = API_REASONS_MESSAGES(companyId, reasonId);

        return this.apiService.delete(api);
      })
    );
  }
}
